import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { doc, getDoc, updateDoc, addDoc, setDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../../firebase';
import { PortvilleRoom, PortvilleResidency, PortvilleRoomWithResidency } from '../../../types/portvilleRoom';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { auth } from '../../../firebase';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';
import { downloadStorageFile } from '../../../utils/storageDownload';

type LotteryDocumentType = 'identification' | 'employment' | 'other';
type LotteryDocuments = Partial<Record<LotteryDocumentType, string>>;

interface MoveInInspectionAttachment {
  id: string;
  name: string;
  storagePath: string;
  size: number;
  contentType: string;
  uploadedAt: Timestamp;
}

const MAX_MOVE_IN_INSPECTION_ATTACHMENTS = 3;
const MAX_MOVE_IN_INSPECTION_ATTACHMENT_SIZE = 5 * 1024 * 1024;

const getDocumentDisplayName = (docType: LotteryDocumentType) => {
  switch (docType) {
    case 'identification':
      return '신분증 사본';
    case 'employment':
      return '재직증명서/사업자등록증';
    case 'other':
      return '기타 서류';
  }
};

const getOriginalFileNameFromStoragePath = (storagePath: string) => {
  const fileName = storagePath.split('/').pop() || storagePath;
  const extracted = fileName.split('_').slice(3).join('_');
  return extracted || fileName;
};

const getMillis = (value: any): number => {
  try {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (value instanceof Date) return value.getTime();
    return new Date(value).getTime();
  } catch {
    return 0;
  }
};

const MoveInInspectionPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  
  const [room, setRoom] = useState<PortvilleRoomWithResidency | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inspectorName, setInspectorName] = useState('');
  const [inspectionNotes] = useState('');
  const [signatureSize, setSignatureSize] = useState({ width: 400, height: 200 });
  const [lotteryDocuments, setLotteryDocuments] = useState<LotteryDocuments>({});
  const [lotteryDocumentsLoading, setLotteryDocumentsLoading] = useState(false);
  const [lotteryDocumentsError, setLotteryDocumentsError] = useState<string | null>(null);
  const [lotteryDocumentsDownloading, setLotteryDocumentsDownloading] = useState<LotteryDocumentType | null>(null);
  const [inspectionRecordId, setInspectionRecordId] = useState<string | null>(null);
  const [inspectionAttachments, setInspectionAttachments] = useState<MoveInInspectionAttachment[]>([]);
  const [newInspectionAttachments, setNewInspectionAttachments] = useState<File[]>([]);
  const [inspectionAttachmentsToDelete, setInspectionAttachmentsToDelete] = useState<MoveInInspectionAttachment[]>([]);
  const [inspectionAttachmentError, setInspectionAttachmentError] = useState('');
  const [inspectionAttachmentSaving, setInspectionAttachmentSaving] = useState(false);
  const [inspectionAttachmentDownloadingId, setInspectionAttachmentDownloadingId] = useState<string | null>(null);
  
  // 추첨 당첨자 정보
  const [lotteryWinnerInfo, setLotteryWinnerInfo] = useState<{
    name: string;
    uid: string;
    contractStartDate: Date;
    contractEndDate: Date;
  } | null>(null);

  const getDefaultContractPeriod = useCallback(() => {
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 729);
    return { startDate, endDate };
  }, []);

  useBackToPortvilleRoomsListOnPop();

  const goToRoomsList = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);
  
  // 점검 항목들
  const [inspectionItems, setInspectionItems] = useState([
    { id: 'room_condition', name: '호실 상태', checked: false, notes: '' },
    { id: 'facilities', name: '시설물 상태', checked: false, notes: '' },
    { id: 'electronics', name: '전자제품 작동', checked: false, notes: '' },
    { id: 'plumbing', name: '배관 상태', checked: false, notes: '' },
    { id: 'safety', name: '안전 시설', checked: false, notes: '' },
    { id: 'cleanliness', name: '청결 상태', checked: false, notes: '' }
  ]);

  // 화면 크기에 따른 서명 패드 크기 조정
  const updateSignatureSize = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setSignatureSize({ width: 320, height: 160 });
    } else {
      setSignatureSize({ width: 400, height: 200 });
    }
  }, []);

  useEffect(() => {
    if (roomId && hasAccess) {
      loadRoom();
    }
    
    // 페이지 로드 시 권한 상태 디버깅
    const debugAuthStatus = async () => {
      console.log('=== 페이지 로드 시 권한 상태 ===');
      console.log('roomId:', roomId);
      console.log('hasAccess (usePortvilleRoomAuth):', hasAccess);
      console.log('authLoading:', authLoading);
      
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          console.log('페이지 로드 시 Custom Claims:', idTokenResult.claims);
        } catch (error) {
          console.error('페이지 로드 시 토큰 조회 오류:', error);
        }
      }
      console.log('=== 페이지 로드 시 권한 상태 종료 ===');
    };
    
    if (!authLoading) {
      debugAuthStatus();
    }
  }, [roomId, hasAccess, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateSignatureSize();
    window.addEventListener('resize', updateSignatureSize);
    return () => window.removeEventListener('resize', updateSignatureSize);
  }, [updateSignatureSize]);

	  // 방 정보가 로드된 후 점검자 이름 설정
	  useEffect(() => {
	    if (room?.currentResident?.name && !inspectorName) {
	      setInspectorName(room.currentResident.name);
	      console.log('방 정보 로드 후 점검자 이름 설정:', room.currentResident.name);
	    } else if (room && !room.currentResident?.name && !inspectorName) {
	      // 입주 기록에 정보가 없으면 추첨 이력에서 조회
	      loadLotteryWinnerInfo(room.roomNumber);
	    }
	  }, [room, inspectorName]);

  // 추첨 이력에서 입주 대상자 정보 조회
  const loadLotteryWinnerInfo = async (
    roomNumber: string
  ): Promise<{ name: string; uid: string } | null> => {
    try {
      console.log('=== 추첨 이력 조회 시작 ===');
      console.log('조회할 호실번호:', roomNumber);
      
      // roomNumber가 문자열 타입이므로 문자열로 직접 조회
      const lotteryQuery = query(
        collection(db, 'portville_lottery_history'),
        where('roomNumber', '==', roomNumber),
        where('status', '==', 'drawn')
      );
      
      const lotterySnapshot = await getDocs(lotteryQuery);
      console.log('조회 결과 개수:', lotterySnapshot.size);
      
      if (!lotterySnapshot.empty) {
        const lotteryDoc = lotterySnapshot.docs[0];
        const lotteryData = lotteryDoc.data();
        
        console.log('발견된 추첨 이력:', {
          roomNumber: lotteryData.roomNumber,
          status: lotteryData.status,
          uid: lotteryData.uid,
          drawnAt: lotteryData.drawnAt
        });
        
        if (lotteryData.uid) {
          console.log('UID로 사용자 프로필 조회:', lotteryData.uid);
          
          // 사용자 프로필에서 이름 조회
          const userProfileDoc = await getDoc(doc(db, 'user_profiles', lotteryData.uid));
          
          if (userProfileDoc.exists()) {
            const userProfile = userProfileDoc.data();
            const userName = userProfile.name || userProfile.displayName;
            
            if (userName) {
              setInspectorName(userName);
              
              // 계약 기간 계산 (현재 날짜부터 729일)
              const contractStartDate = new Date();
              const contractEndDate = new Date();
              contractEndDate.setDate(contractEndDate.getDate() + 729);
              
              // 추첨 당첨자 정보 저장
              setLotteryWinnerInfo({
                name: userName,
                uid: lotteryData.uid,
                contractStartDate,
                contractEndDate
              });
              
              console.log('✅ 추첨 당첨자 정보 설정:', {
                name: userName,
                uid: lotteryData.uid,
                contractPeriod: `${contractStartDate.toLocaleDateString('ko-KR')} ~ ${contractEndDate.toLocaleDateString('ko-KR')}`
              });

              return { name: userName, uid: lotteryData.uid };
            } else {
              console.warn('❌ 사용자 프로필에서 이름을 찾을 수 없습니다.');
              console.log('사용자 프로필 데이터:', userProfile);
            }
          } else {
            console.warn('❌ 사용자 프로필을 찾을 수 없습니다. UID:', lotteryData.uid);
          }
        } else {
          console.warn('❌ 추첨 이력에 UID가 없습니다.');
        }
      } else {
        console.warn('❌ 해당 호실의 추첨 완료(drawn) 기록을 찾을 수 없습니다.');
        
        // 디버깅: status 조건 없이 해당 호실의 모든 기록 조회
        const allRoomQuery = query(
          collection(db, 'portville_lottery_history'),
          where('roomNumber', '==', roomNumber)
        );
        const allRoomSnapshot = await getDocs(allRoomQuery);
        console.log('해당 호실의 모든 기록 개수:', allRoomSnapshot.size);
        
        if (!allRoomSnapshot.empty) {
          allRoomSnapshot.forEach(doc => {
            const data = doc.data();
            console.log('호실 기록:', {
              id: doc.id,
              roomNumber: data.roomNumber,
              status: data.status,
              uid: data.uid,
              drawnAt: data.drawnAt
            });
          });
        }
      }
      
      console.log('=== 추첨 이력 조회 종료 ===');
      
    } catch (error) {
      console.error('❌ 추첨 이력 조회 중 오류:', error);
    }

    return null;
  };

  const loadLotteryDocumentsForRoom = async (args: {
    roomId: string;
    roomNumber: string;
    preferredUid?: string;
  }) => {
    try {
      setLotteryDocumentsLoading(true);
      setLotteryDocumentsError(null);
      setLotteryDocuments({});

      const getMillis = (value: any): number => {
        try {
          if (!value) return 0;
          if (typeof value.toMillis === 'function') return value.toMillis();
          if (typeof value.toDate === 'function') return value.toDate().getTime();
          return 0;
        } catch {
          return 0;
        }
      };

      const lotteryRef = collection(db, 'portville_lottery_history');

      // roomId 기준 조회 (권장), 없으면 roomNumber로 fallback
      const byRoomIdSnapshot = await getDocs(query(lotteryRef, where('roomId', '==', args.roomId)));
      const snapshot = !byRoomIdSnapshot.empty
        ? byRoomIdSnapshot
        : await getDocs(query(lotteryRef, where('roomNumber', '==', args.roomNumber)));

      const docs = snapshot.docs
        .map((d) => ({ id: d.id, data: d.data() }))
        .sort((a, b) => getMillis(b.data.drawnAt) - getMillis(a.data.drawnAt));

      const preferred = args.preferredUid ? docs.find((d) => d.data.uid === args.preferredUid) : null;
      const selected = preferred || docs[0] || null;

      const documents = (selected?.data?.documents || {}) as LotteryDocuments;
      setLotteryDocuments(documents);
    } catch (lotteryDocError) {
      console.warn('계약 전 제출 서류 조회 실패:', lotteryDocError);
      setLotteryDocumentsError('계약 전 제출 서류를 불러오지 못했습니다.');
    } finally {
      setLotteryDocumentsLoading(false);
    }
  };

  const loadMoveInInspectionRecord = async (targetRoomId: string) => {
    try {
      const snapshot = await getDocs(query(collection(db, 'portville_inspections'), where('roomId', '==', targetRoomId)));
      const records = snapshot.docs
        .map((inspectionDoc) => ({ id: inspectionDoc.id, data: inspectionDoc.data() }))
        .filter(({ data }) => !data.inspectionType || data.inspectionType === 'move-in')
        .sort((a, b) => getMillis(b.data.inspectionSignedAt || b.data.createdAt) - getMillis(a.data.inspectionSignedAt || a.data.createdAt));

      const latest = records[0];
      if (!latest) {
        setInspectionRecordId(null);
        setInspectionAttachments([]);
        return;
      }

      setInspectionRecordId(latest.id);
      setInspectionAttachments(Array.isArray(latest.data.attachments) ? latest.data.attachments : []);
      setInspectionAttachmentsToDelete([]);
      setNewInspectionAttachments([]);
      setInspectionAttachmentError('');
    } catch (inspectionError) {
      console.warn('입주점검 기록 조회 실패:', inspectionError);
    }
  };

	  const loadRoom = async () => {
	    if (!roomId) return;
	    
	    try {
	      setLoading(true);
      
      // 1. 호실 정보 조회
      const roomDoc = await getDoc(doc(db, 'portville_rooms', roomId));
      if (!roomDoc.exists()) {
        setError('호실을 찾을 수 없습니다.');
        return;
      }

      const roomData = roomDoc.data() as PortvilleRoom;
      
      // 2. 현재 입주 기록 조회
      let currentResidency: PortvilleResidency | undefined;
      if (roomData.currentResidencyId) {
        const residencyDoc = await getDoc(doc(db, 'portville_residencies', roomData.currentResidencyId));
        if (residencyDoc.exists()) {
          const residencyData = residencyDoc.data();
          currentResidency = {
            id: residencyDoc.id,
            roomId: residencyData.roomId,
            roomNumber: residencyData.roomNumber,
            residentUid: residencyData.residentUid,
            residentInfo: residencyData.residentInfo,
            occupancyPeriod: residencyData.occupancyPeriod ? {
              startDate: residencyData.occupancyPeriod.startDate.toDate(),
              endDate: residencyData.occupancyPeriod.endDate.toDate()
            } : undefined,
            roommateInfo: residencyData.roommateInfo,
            vehicleInfo: residencyData.vehicleInfo,
            notes: residencyData.notes,
            signatureName: residencyData.signatureName,
            signatureDate: residencyData.signatureDate ? residencyData.signatureDate.toDate() : undefined,
            signatureUrl: residencyData.signatureUrl,
            contractSignedAt: residencyData.contractSignedAt ? residencyData.contractSignedAt.toDate() : undefined,
            moveInInspectionSignedAt: residencyData.moveInInspectionSignedAt ? residencyData.moveInInspectionSignedAt.toDate() : undefined,
            moveInInspectionSignatureUrl: residencyData.moveInInspectionSignatureUrl,
            status: residencyData.status,
            createdAt: residencyData.createdAt.toDate(),
            updatedAt: residencyData.updatedAt.toDate(),
            completedAt: residencyData.completedAt ? residencyData.completedAt.toDate() : undefined
          };
        }
      }

      // 3. 통합 뷰 생성
      const combinedRoom: PortvilleRoomWithResidency = {
          id: roomDoc.id,
        roomNumber: roomData.roomNumber,
        roomType: roomData.roomType || '호실',
        status: roomData.status,
        createdAt: (roomData.createdAt as any).toDate ? (roomData.createdAt as any).toDate() : roomData.createdAt as Date,
        updatedAt: (roomData.updatedAt as any).toDate ? (roomData.updatedAt as any).toDate() : roomData.updatedAt as Date,
        currentResidencyId: roomData.currentResidencyId,
        facilityInfo: roomData.facilityInfo
      };

      if (currentResidency) {
	        combinedRoom.currentResident = currentResidency.residentInfo;
	        combinedRoom.residentUid = currentResidency.residentUid;
	        combinedRoom.occupancyPeriod = currentResidency.occupancyPeriod;
        combinedRoom.roommateInfo = currentResidency.roommateInfo;
        combinedRoom.vehicleInfo = currentResidency.vehicleInfo;
        combinedRoom.notes = currentResidency.notes;
        combinedRoom.signatureName = currentResidency.signatureName;
        combinedRoom.signatureDate = currentResidency.signatureDate;
        combinedRoom.signatureUrl = currentResidency.signatureUrl;
        combinedRoom.contractSignedAt = currentResidency.contractSignedAt;
	        combinedRoom.moveInInspectionSignedAt = currentResidency.moveInInspectionSignedAt;
	        combinedRoom.moveInInspectionSignatureUrl = currentResidency.moveInInspectionSignatureUrl;
      }
	        
	      setRoom(combinedRoom);
      await loadMoveInInspectionRecord(combinedRoom.id);
        
      // 계약진행 상태가 아닌 경우에만 에러 처리 (계약 완료 전에도 입주점검 가능)
      if (combinedRoom.status !== 'contracting') {
        setError('계약진행 상태의 호실만 입주점검이 가능합니다.');
        return;
        }
        
	      // 기본 점검자 이름을 입주 대상자 이름으로 설정
	      if (combinedRoom.currentResident?.name) {
	        setInspectorName(combinedRoom.currentResident.name);
	        console.log('점검자 이름을 입주 대상자로 설정:', combinedRoom.currentResident.name);
	        
	        // 입주 기록이 있는 경우에도 저장 로직에서 사용하는 lotteryWinnerInfo를 보강
	        if (combinedRoom.residentUid) {
	          const period = combinedRoom.occupancyPeriod || getDefaultContractPeriod();
	          setLotteryWinnerInfo({
	            name: combinedRoom.currentResident.name,
	            uid: combinedRoom.residentUid,
	            contractStartDate: period.startDate,
	            contractEndDate: period.endDate
	          });
	        }
	      } else {
	        console.warn('입주 기록에 입주 대상자 정보가 없습니다. 추첨 이력에서 조회를 시도합니다.');
	        // 추첨 이력에서 입주 대상자 정보 조회
	        const winner = await loadLotteryWinnerInfo(combinedRoom.roomNumber);
        await loadLotteryDocumentsForRoom({
          roomId: combinedRoom.id,
          roomNumber: combinedRoom.roomNumber,
          preferredUid: winner?.uid
        });
        return;
      }

      await loadLotteryDocumentsForRoom({
        roomId: combinedRoom.id,
        roomNumber: combinedRoom.roomNumber,
        preferredUid: combinedRoom.residentUid
      });
    } catch (error) {
      console.error('Error loading room:', error);
      setError('호실 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const updateInspectionItem = (id: string, field: 'checked' | 'notes', value: boolean | string) => {
    setInspectionItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const sanitizeFileName = (fileName: string) =>
    fileName.replace(/[^a-zA-Z0-9._-]/g, '_');

  const getActiveInspectionAttachments = () =>
    inspectionAttachments.filter(
      attachment => !inspectionAttachmentsToDelete.some(target => target.id === attachment.id)
    );

  const validateInspectionAttachment = (file: File) => {
    if (file.size > MAX_MOVE_IN_INSPECTION_ATTACHMENT_SIZE) {
      return '입주점검 첨부파일은 각 5MB 이하만 업로드할 수 있습니다.';
    }

    return '';
  };

  const uploadInspectionAttachment = async (targetInspectionId: string, file: File): Promise<MoveInInspectionAttachment> => {
    const safeName = sanitizeFileName(file.name);
    const attachmentId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const storagePath = `move_in_inspection_attachments/${roomId}/${targetInspectionId}/${attachmentId}_${safeName}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file);

    return {
      id: attachmentId,
      name: file.name,
      storagePath,
      size: file.size,
      contentType: file.type || 'application/octet-stream',
      uploadedAt: Timestamp.now()
    };
  };

  const deleteInspectionAttachmentFile = async (attachment: MoveInInspectionAttachment) => {
    try {
      await deleteObject(ref(storage, attachment.storagePath));
    } catch (deleteError) {
      console.error('입주점검 첨부파일 삭제 실패:', deleteError);
    }
  };

  const handleInspectionAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) {
      return;
    }

    const activeExistingAttachments = getActiveInspectionAttachments();
    if (activeExistingAttachments.length + newInspectionAttachments.length + selectedFiles.length > MAX_MOVE_IN_INSPECTION_ATTACHMENTS) {
      setInspectionAttachmentError(`입주점검 첨부파일은 최대 ${MAX_MOVE_IN_INSPECTION_ATTACHMENTS}개까지 등록할 수 있습니다.`);
      event.target.value = '';
      return;
    }

    for (const file of selectedFiles) {
      const validationError = validateInspectionAttachment(file);
      if (validationError) {
        setInspectionAttachmentError(validationError);
        event.target.value = '';
        return;
      }
    }

    setInspectionAttachmentError('');
    setNewInspectionAttachments(prev => [...prev, ...selectedFiles]);
    event.target.value = '';
  };

  const handleRemoveNewInspectionAttachment = (targetIndex: number) => {
    setNewInspectionAttachments(prev => prev.filter((_, index) => index !== targetIndex));
  };

  const handleRemoveExistingInspectionAttachment = (attachment: MoveInInspectionAttachment) => {
    setInspectionAttachmentsToDelete(prev => (
      prev.some(target => target.id === attachment.id)
        ? prev
        : [...prev, attachment]
    ));
  };

  const handleRestoreExistingInspectionAttachment = (attachment: MoveInInspectionAttachment) => {
    setInspectionAttachmentsToDelete(prev => prev.filter(target => target.id !== attachment.id));
  };

  const downloadInspectionAttachment = async (attachment: MoveInInspectionAttachment) => {
    try {
      setInspectionAttachmentDownloadingId(attachment.id);
      const filenameBase = attachment.name.replace(/\.[^/.]+$/, '');
      await downloadStorageFile(
        attachment.storagePath,
        `${room?.roomNumber || ''}호_입주점검_${filenameBase || '첨부파일'}`
      );
    } catch (downloadError) {
      console.error('입주점검 첨부파일 다운로드 실패:', downloadError);
      alert('첨부파일 다운로드 중 오류가 발생했습니다.');
    } finally {
      setInspectionAttachmentDownloadingId(null);
    }
  };

  const saveCompletedInspectionAttachments = async () => {
    if (!inspectionRecordId) {
      alert('입주점검 기록을 찾을 수 없습니다.');
      return;
    }

    const activeExistingAttachments = getActiveInspectionAttachments();
    if (activeExistingAttachments.length + newInspectionAttachments.length > MAX_MOVE_IN_INSPECTION_ATTACHMENTS) {
      setInspectionAttachmentError(`입주점검 첨부파일은 최대 ${MAX_MOVE_IN_INSPECTION_ATTACHMENTS}개까지 등록할 수 있습니다.`);
      return;
    }

    try {
      setInspectionAttachmentSaving(true);
      const uploadedAttachments = await Promise.all(
        newInspectionAttachments.map(file => uploadInspectionAttachment(inspectionRecordId, file))
      );
      const nextAttachments = [...activeExistingAttachments, ...uploadedAttachments];

      await updateDoc(doc(db, 'portville_inspections', inspectionRecordId), {
        attachments: nextAttachments,
        updatedAt: Timestamp.now()
      });

      await Promise.all(inspectionAttachmentsToDelete.map(deleteInspectionAttachmentFile));

      setInspectionAttachments(nextAttachments);
      setNewInspectionAttachments([]);
      setInspectionAttachmentsToDelete([]);
      setInspectionAttachmentError('');
      alert('입주점검 첨부파일이 저장되었습니다.');
    } catch (saveError) {
      console.error('입주점검 첨부파일 저장 실패:', saveError);
      alert('첨부파일 저장 중 오류가 발생했습니다.');
    } finally {
      setInspectionAttachmentSaving(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const saveInspection = async () => {
	    if (!room || !sigCanvasRef.current || !inspectorName.trim()) {
	      alert('서명과 점검자 이름을 모두 입력해주세요.');
	      return;
	    }

	    if (sigCanvasRef.current.isEmpty()) {
	      alert('서명을 입력해주세요.');
	      return;
	    }

    if (newInspectionAttachments.length > MAX_MOVE_IN_INSPECTION_ATTACHMENTS) {
      setInspectionAttachmentError(`입주점검 첨부파일은 최대 ${MAX_MOVE_IN_INSPECTION_ATTACHMENTS}개까지 등록할 수 있습니다.`);
      return;
    }

	    const fallbackPeriod = getDefaultContractPeriod();
	    const resolvedWinnerInfo = lotteryWinnerInfo || (
	      room.residentUid
	        ? {
	            name: room.currentResident?.name || inspectorName,
	            uid: room.residentUid,
	            contractStartDate: room.occupancyPeriod?.startDate || fallbackPeriod.startDate,
	            contractEndDate: room.occupancyPeriod?.endDate || fallbackPeriod.endDate
	          }
	        : null
	    );
	    
	    if (!resolvedWinnerInfo) {
	      alert('추첨 당첨자 정보를 찾을 수 없습니다. 페이지를 새로고침해 주세요.');
	      return;
	    }

    try {
      setSaving(true);
      
      // === 권한 디버깅 시작 ===
      console.log('=== 권한 디버깅 시작 ===');
      
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log('현재 사용자 UID:', currentUser.uid);
        console.log('현재 사용자 이메일:', currentUser.email);
        
        // ID 토큰 및 Custom Claims 확인
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          console.log('ID 토큰 발급 시간:', new Date(idTokenResult.issuedAtTime));
          console.log('ID 토큰 만료 시간:', new Date(idTokenResult.expirationTime));
          console.log('Custom Claims:', idTokenResult.claims);
          
          // 권한 체크
          const hasAdmin = idTokenResult.claims.admin === true;
          const hasPortvilleAdmin = idTokenResult.claims.portville_admin === true;
          const hasRoomAdmin = idTokenResult.claims.room_admin === true;
          
          console.log('권한 체크 결과:');
          console.log('  - admin:', hasAdmin);
          console.log('  - portville_admin:', hasPortvilleAdmin);
          console.log('  - room_admin:', hasRoomAdmin);
          console.log('  - 총 권한 있음:', hasAdmin || hasPortvilleAdmin || hasRoomAdmin);
          
          // 토큰이 너무 오래된 경우 새로고침 시도
          const tokenAge = Date.now() - new Date(idTokenResult.issuedAtTime).getTime();
          console.log('토큰 나이 (분):', Math.floor(tokenAge / (1000 * 60)));
          
          if (tokenAge > 60 * 60 * 1000) { // 1시간 이상된 토큰
            console.log('토큰이 오래되어 새로고침을 시도합니다...');
            await currentUser.getIdToken(true); // 강제 새로고침
            const newTokenResult = await currentUser.getIdTokenResult();
            console.log('새로고침된 토큰 Claims:', newTokenResult.claims);
          }
          
        } catch (tokenError) {
          console.error('토큰 조회 오류:', tokenError);
        }
      } else {
        console.error('현재 사용자가 없습니다!');
      }
      
      // usePortvilleRoomAuth 상태 확인
      console.log('usePortvilleRoomAuth 상태:');
      console.log('  - loading:', authLoading);
      console.log('  - hasAccess:', hasAccess);
      
             // 권한 불일치 체크 및 경고
       if (currentUser) {
         const idTokenResult = await currentUser.getIdTokenResult();
         const hasFirestoreAccess = hasAccess; // usePortvilleRoomAuth에서 가져온 값
         const hasCustomClaims = idTokenResult.claims.admin === true || 
                                idTokenResult.claims.portville_admin === true || 
                                idTokenResult.claims.room_admin === true;
         
         console.log('권한 불일치 체크:');
         console.log('  - Firestore 기반 권한 (hasAccess):', hasFirestoreAccess);
         console.log('  - Custom Claims 기반 권한:', hasCustomClaims);
         
                             if (hasFirestoreAccess && !hasCustomClaims) {
            console.warn('⚠️  권한 불일치 감지: Firestore에서는 권한이 있지만 Custom Claims가 없습니다.');
            console.warn('⚠️  Storage 접근을 위해서는 관리자가 Custom Claims를 설정해야 합니다.');
            console.log('🔄  관리자 페이지이므로 자동으로 Custom Claims를 설정합니다...');
            
            try {
              // 관리자 권한 설정 함수 호출 (setAdminClaims 사용)
              const setAdminClaims = httpsCallable(functions, 'setAdminClaims');
              const result = await setAdminClaims({ uid: currentUser.uid });
              
              // 결과 타입 정의
              const resultData = result.data as { success: boolean; message: string; claims?: any };
              
              console.log('✅ Custom Claims 설정 결과:', resultData);
              
              if (resultData.success) {
                console.log('✅ 관리자 권한이 성공적으로 설정되었습니다!');
                console.log('🔄 토큰을 새로고침하고 다시 시도합니다...');
                
                // 토큰 새로고침
                await currentUser.getIdToken(true);
                
                // 권한 재확인
                const newTokenResult = await currentUser.getIdTokenResult();
                const newHasCustomClaims = newTokenResult.claims.admin === true || 
                                         newTokenResult.claims.portville_admin === true || 
                                         newTokenResult.claims.room_admin === true;
                
                console.log('🔍 새로고침된 Custom Claims:', newTokenResult.claims);
                console.log('🔍 새 권한 상태:', newHasCustomClaims);
                
                if (!newHasCustomClaims) {
                  console.warn('⚠️  토큰 새로고침 후에도 권한이 확인되지 않습니다. 잠시 후 다시 시도하거나 페이지를 새로고침해주세요.');
                }
                
                // 권한이 설정되었으므로 계속 진행
                console.log('✅ Custom Claims 설정 완료, Storage 업로드를 계속 진행합니다.');
              } else {
                console.error('❌ Custom Claims 설정 실패:', resultData.message);
                alert(`관리자 권한 설정에 실패했습니다.\n\n오류: ${resultData.message}\n\n페이지를 새로고침하거나 시스템 관리자에게 문의해주세요.`);
                return;
              }
            } catch (error) {
              console.error('❌ Custom Claims 설정 중 오류:', error);
              alert(`관리자 권한 설정 중 오류가 발생했습니다.\n\n오류: ${error instanceof Error ? error.message : '알 수 없는 오류'}\n\n페이지를 새로고침하거나 시스템 관리자에게 문의해주세요.`);
              return;
            }
          }
       }
       
       console.log('=== 권한 디버깅 종료 ===');
       // === 권한 디버깅 종료 ===
      
      // 서명 이미지를 블롭으로 변환
      const canvas = sigCanvasRef.current.getCanvas();
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/png');
      });

      // Firebase Storage에 서명 이미지 업로드
      const signatureRef = ref(storage, `move_in_inspection_signatures/${roomId}_${Date.now()}.png`);
      console.log('Storage 업로드 시도 경로:', signatureRef.fullPath);
      
      await uploadBytes(signatureRef, blob);
      console.log('✅ Storage 업로드 성공!');
      
      const signatureUrl = await getDownloadURL(signatureRef);
      console.log('✅ 다운로드 URL 생성 성공:', signatureUrl);

      const now = Timestamp.now();

	      let residencyId = room.currentResidencyId;

	      // 1. 입주 기록이 없으면 생성, 있으면 업데이트
	      if (!residencyId) {
	        if (!resolvedWinnerInfo) {
	          alert('입주 대상자 정보를 찾을 수 없습니다. 먼저 계약을 생성하거나(입주자 지정) 추첨 이력을 확인해주세요.');
	          return;
	        }

	        // 사용자 프로필에서 상세 정보 가져오기
	        let residentInfo = {
	          name: resolvedWinnerInfo.name,
	          phoneNumber: '',
	          organization: ''
	        };
	        
	        try {
	          const userProfileRef = doc(db, 'user_profiles', resolvedWinnerInfo.uid);
	          const userProfileDoc = await getDoc(userProfileRef);
	          
	          if (userProfileDoc.exists()) {
	            const userProfile = userProfileDoc.data();
	            residentInfo = {
	              name: userProfile.name || resolvedWinnerInfo.name,
	              phoneNumber: userProfile.phoneNumber || '',
	              organization: userProfile.organization || ''
	            };
	            console.log('✅ 사용자 프로필에서 입주자 정보 조회 완료:', residentInfo);
	          }
	        } catch (profileError) {
	          console.warn('사용자 프로필 조회 중 오류:', profileError);
	        }
	        
	        // 새로운 입주 기록 생성
	        const newResidencyData = {
	          roomId: roomId!,
	          roomNumber: room.roomNumber,
	          residentUid: resolvedWinnerInfo.uid, // Firestore 규칙에서 필수 요구
	          residentInfo: residentInfo, // user_profiles에서 가져온 정보 사용
	          occupancyPeriod: {
	            startDate: Timestamp.fromDate(resolvedWinnerInfo.contractStartDate),
	            endDate: Timestamp.fromDate(resolvedWinnerInfo.contractEndDate)
	          },
	          moveInInspectionSignedAt: now,
	          moveInInspectionSignatureUrl: signatureUrl,
	          status: 'contracting' as const,
          createdAt: now,
          updatedAt: now
        };

        const newResidencyRef = await addDoc(collection(db, 'portville_residencies'), newResidencyData);
        residencyId = newResidencyRef.id;

        // 호실의 currentResidencyId 업데이트
        await updateDoc(doc(db, 'portville_rooms', roomId!), {
          currentResidencyId: residencyId,
          updatedAt: now
        });

        console.log('✅ 새로운 입주 기록 생성:', residencyId);
	      } else {
	        // 기존 입주 기록 업데이트
	        await updateDoc(doc(db, 'portville_residencies', residencyId), {
	          moveInInspectionSignedAt: now,
	          moveInInspectionSignatureUrl: signatureUrl,
	          updatedAt: now
	        });

	        console.log('✅ 기존 입주 기록 업데이트:', residencyId);
	      }

		      const residentUid = room.residentUid || resolvedWinnerInfo.uid || '';
		      const residentInfo = room.currentResident || { name: resolvedWinnerInfo.name, phoneNumber: '', organization: '' };

	        // 입주점검은 동일 회차의 중간 단계이므로 lottery는 drawn 상태로 유지한다.
	        // lottery의 suppressed/archived 처리는 계약 날인 또는 계약 포기 확정 시점에 수행한다.

      const defaultPeriod = getDefaultContractPeriod();
		      const occupancyPeriod = room.occupancyPeriod
		        ? {
		            startDate: Timestamp.fromDate(room.occupancyPeriod.startDate),
	            endDate: Timestamp.fromDate(room.occupancyPeriod.endDate)
	          }
	        : resolvedWinnerInfo
	          ? {
	              startDate: Timestamp.fromDate(resolvedWinnerInfo.contractStartDate),
	              endDate: Timestamp.fromDate(resolvedWinnerInfo.contractEndDate)
	            }
	          : {
	              startDate: Timestamp.fromDate(defaultPeriod.startDate),
	              endDate: Timestamp.fromDate(defaultPeriod.endDate)
	            };

      // 2. 별도 점검 기록 저장
      const inspectionRef = doc(collection(db, 'portville_inspections'));
      const uploadedInspectionAttachments = await Promise.all(
        newInspectionAttachments.map(file => uploadInspectionAttachment(inspectionRef.id, file))
      );

      const inspectionData = {
        inspectionType: 'move-in',
        roomId: roomId!,
        roomNumber: room.roomNumber,
        residencyId: residencyId, // 생성된 또는 기존 입주 기록 ID
        residentInfo: residentInfo,
        residentUid: residentUid,
        occupancyPeriod,
        inspectionItems: inspectionItems.map(item => ({
          id: item.id,
          name: item.name,
          checked: item.checked,
          notes: item.notes
        })),
        inspectorName,
        inspectionNotes,
        attachments: uploadedInspectionAttachments,
        signatureUrl,
        inspectionSignedAt: now,
        contractInfo: {
          contractSignedAt: room.contractSignedAt ? Timestamp.fromDate(room.contractSignedAt) : null,
          signatureName: room.signatureName || '',
          signatureUrl: room.signatureUrl || '',
          contractStatus: room.contractSignedAt ? 'completed' : 'pending'
        },
        createdAt: now
      };

      console.log('=== 점검 데이터 저장 시도 ===');
      console.log('inspectionData:', inspectionData);
      console.log('필수 필드 확인:');
      console.log('  - roomId:', inspectionData.roomId);
      console.log('  - roomNumber:', inspectionData.roomNumber);
      console.log('  - residentInfo:', inspectionData.residentInfo);
      console.log('  - inspectionItems:', inspectionData.inspectionItems);
      console.log('  - inspectorName:', inspectionData.inspectorName);
      console.log('  - signatureUrl:', inspectionData.signatureUrl);
      console.log('  - inspectionSignedAt:', inspectionData.inspectionSignedAt);
      console.log('  - createdAt:', inspectionData.createdAt);

      await setDoc(inspectionRef, inspectionData);

      console.log('✅ 점검 기록 저장 완료');

      alert('입주점검이 완료되었습니다. 이제 계약서 날인을 진행해주세요.');
      goToRoomsList();
      
    } catch (error) {
      console.error('점검 저장 오류:', error);
      
      // Storage 권한 오류인 경우 구체적인 안내 제공
      if (error instanceof Error && error.message.includes('storage/unauthorized')) {
        console.error('❌ Firebase Storage 권한 오류 상세:', {
          errorCode: 'storage/unauthorized',
          errorMessage: error.message,
          timestamp: new Date().toISOString()
        });
        
        alert('Storage 접근 권한 오류가 발생했습니다.\n\n가능한 원인:\n1. Custom Claims가 설정되지 않음\n2. 토큰이 만료됨\n3. Storage 규칙 설정 오류\n\n콘솔의 디버그 로그를 확인해주세요.');
      } else {
        alert('점검 저장 중 오류가 발생했습니다.\n\n오류: ' + (error instanceof Error ? error.message : '알 수 없는 오류'));
      }
    } finally {
      setSaving(false);
    }
  };

  const renderInspectionAttachmentManager = (showSaveButton: boolean) => {
    const hasPendingChanges = newInspectionAttachments.length > 0 || inspectionAttachmentsToDelete.length > 0;

    return (
      <div className="mb-8 p-4 bg-white rounded-md border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">입주점검 첨부파일</h2>
            <p className="text-sm text-gray-600">
              최대 {MAX_MOVE_IN_INSPECTION_ATTACHMENTS}개, 파일당 5MB 이하로 등록할 수 있습니다.
            </p>
          </div>
          <label className="inline-flex items-center justify-center px-4 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800 cursor-pointer">
            파일 선택
            <input
              type="file"
              multiple
              onChange={handleInspectionAttachmentChange}
              className="hidden"
              disabled={saving || inspectionAttachmentSaving}
            />
          </label>
        </div>

        {inspectionAttachmentError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {inspectionAttachmentError}
          </div>
        )}

        <div className="mt-4 space-y-2">
          {inspectionAttachments.length === 0 && newInspectionAttachments.length === 0 && (
            <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded p-4">
              등록된 첨부파일이 없습니다.
            </div>
          )}

          {inspectionAttachments.map((attachment) => {
            const isMarkedForDeletion = inspectionAttachmentsToDelete.some(target => target.id === attachment.id);
            const isDownloading = inspectionAttachmentDownloadingId === attachment.id;

            return (
              <div
                key={attachment.id}
                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border rounded p-3 ${
                  isMarkedForDeletion ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="min-w-0">
                  <div className={`text-sm font-medium truncate ${isMarkedForDeletion ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                    {attachment.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(attachment.size / 1024 / 1024).toFixed(2)}MB
                    {isMarkedForDeletion && <span className="ml-2 text-red-600">삭제 예정</span>}
                  </div>
                </div>

                <div className="flex gap-2">
                  {!isMarkedForDeletion && (
                    <button
                      type="button"
                      onClick={() => downloadInspectionAttachment(attachment)}
                      disabled={Boolean(inspectionAttachmentDownloadingId)}
                      className="px-3 py-1.5 rounded text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500"
                    >
                      {isDownloading ? '확인 중...' : '확인'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => (
                      isMarkedForDeletion
                        ? handleRestoreExistingInspectionAttachment(attachment)
                        : handleRemoveExistingInspectionAttachment(attachment)
                    )}
                    disabled={saving || inspectionAttachmentSaving}
                    className={`px-3 py-1.5 rounded text-sm ${
                      isMarkedForDeletion
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    {isMarkedForDeletion ? '복원' : '삭제'}
                  </button>
                </div>
              </div>
            );
          })}

          {newInspectionAttachments.map((file, index) => (
            <div key={`${file.name}-${file.lastModified}-${index}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-blue-200 rounded p-3 bg-blue-50">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{file.name}</div>
                <div className="text-xs text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)}MB
                  <span className="ml-2 text-blue-600">저장 예정</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveNewInspectionAttachment(index)}
                disabled={saving || inspectionAttachmentSaving}
                className="px-3 py-1.5 rounded text-sm bg-red-100 text-red-700 hover:bg-red-200"
              >
                삭제
              </button>
            </div>
          ))}
        </div>

        {showSaveButton && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={saveCompletedInspectionAttachments}
              disabled={!hasPendingChanges || inspectionAttachmentSaving}
              className={`px-4 py-2 rounded-md text-sm ${
                !hasPendingChanges || inspectionAttachmentSaving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {inspectionAttachmentSaving ? '저장 중...' : '첨부파일 변경사항 저장'}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">호실 정보를 불러오는 중...</div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error || '호실을 찾을 수 없습니다.'}</div>
          <button
            onClick={goToRoomsList}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            호실 관리로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 이미 점검이 완료된 경우
  if (room.moveInInspectionSignedAt) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="text-green-800">
            이미 입주점검이 완료된 호실입니다. 
            (완료일: {room.moveInInspectionSignedAt.toLocaleString('ko-KR')})
          </div>
        </div>
        <div className="mt-6">
          {renderInspectionAttachmentManager(true)}
        </div>
        <button
          onClick={goToRoomsList}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          호실 관리로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">입주점검</h1>
        <p className="mt-2 text-sm text-gray-600">
          {room.roomNumber}호 계약 전 시설물 상태를 점검하고 서명해주세요.
        </p>
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                입주점검 완료 후 계약서 날인을 진행합니다.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-8">
          {/* 호실 정보 */}
          <div className="mb-8 p-4 bg-blue-50 rounded-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">호실 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">호실번호:</span>
                <span className="ml-2">{room.roomNumber}호</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">입주자:</span>
                <span className="ml-2">
                  {room.currentResident?.name || lotteryWinnerInfo?.name || '정보 없음'}
                  {lotteryWinnerInfo?.name && !room.currentResident?.name && (
                    <span className="ml-1 text-xs text-blue-600">(추첨 당첨자)</span>
                  )}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">계약기간:</span>
                <span className="ml-2">
                  {room.occupancyPeriod 
                    ? `${room.occupancyPeriod.startDate.toLocaleDateString('ko-KR')} ~ ${room.occupancyPeriod.endDate.toLocaleDateString('ko-KR')}`
                    : lotteryWinnerInfo
                    ? `${lotteryWinnerInfo.contractStartDate.toLocaleDateString('ko-KR')} ~ ${lotteryWinnerInfo.contractEndDate.toLocaleDateString('ko-KR')}`
                    : '정보 없음'
                  }
                  {lotteryWinnerInfo && !room.occupancyPeriod && (
                    <span className="ml-1 text-xs text-blue-600">(예정)</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* 계약 전 제출 서류 (추첨 후 업로드) */}
          <div className="mb-8 p-4 bg-white rounded-md border border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">계약 전 제출 서류</h2>
                <p className="text-sm text-gray-600">
                  사용자가 계약 진행 전에 업로드한 서류입니다. 입주 기록 작성 전에 서류를 확인하세요.
                </p>
              </div>
              {lotteryDocumentsLoading && <div className="text-sm text-gray-500">불러오는 중...</div>}
            </div>

            {lotteryDocumentsError && (
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                {lotteryDocumentsError}
              </div>
            )}

            <div className="mt-4 space-y-2">
              {(['identification', 'employment', 'other'] as const).map((type) => {
                const storagePath = lotteryDocuments[type];
                const isRequired = type === 'identification' || type === 'employment';
                const isDownloading = lotteryDocumentsDownloading === type;

                return (
                  <div key={type} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-gray-200 rounded p-3 bg-gray-50">
                    <div className="text-sm text-gray-800">
                      <span className="font-medium">{getDocumentDisplayName(type)}</span>
                      {isRequired && <span className="ml-2 text-xs text-red-600">(필수)</span>}
                      {storagePath ? (
                        <span className="ml-2 text-xs text-gray-500">{getOriginalFileNameFromStoragePath(storagePath)}</span>
                      ) : (
                        <span className="ml-2 text-xs text-gray-500">미제출</span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {!storagePath ? null : (
                        <button
                          onClick={async () => {
                            if (!storagePath) return;
                            try {
                              setLotteryDocumentsDownloading(type);
                              const residentName = room.currentResident?.name || lotteryWinnerInfo?.name || '입주자';
                              await downloadStorageFile(
                                storagePath,
                                `${room.roomNumber}호_${residentName}_${getDocumentDisplayName(type)}`
                              );
                            } catch (downloadError) {
                              console.error('계약 전 제출 서류 다운로드 실패:', downloadError);
                              alert('서류 다운로드 중 오류가 발생했습니다.');
                            } finally {
                              setLotteryDocumentsDownloading(null);
                            }
                          }}
                          disabled={Boolean(lotteryDocumentsDownloading)}
                          className={`px-3 py-1.5 rounded text-sm ${
                            Boolean(lotteryDocumentsDownloading)
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isDownloading ? '다운로드 중...' : '다운로드'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {renderInspectionAttachmentManager(false)}

          {/* 점검 진행 상황 */}
          <div className="mb-6 p-4 bg-blue-50 rounded-md">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">점검 진행 상황</h2>
              <div className="text-sm text-gray-600">
                {inspectionItems.filter(item => item.checked).length} / {inspectionItems.length} 완료
              </div>
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(inspectionItems.filter(item => item.checked).length / inspectionItems.length) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* 점검 항목들 */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">시설물 점검 항목</h2>
            
            {inspectionItems.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-md p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id={`check-${item.id}`}
                            checked={item.checked}
                            onChange={(e) => updateInspectionItem(item.id, 'checked', e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <label htmlFor={`check-${item.id}`} className="font-medium text-gray-900 cursor-pointer">
                      {item.name}
                          </label>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.checked ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'
                        }`}>
                          {item.checked ? '확인완료' : '미확인'}
                        </span>
                </div>
                <div className="mt-3 pl-7">
                  <label htmlFor={`notes-${item.id}`} className="block text-sm font-medium text-gray-700 mb-1">
                    메모
                  </label>
                  <textarea
                    id={`notes-${item.id}`}
                    value={item.notes}
                    onChange={(e) => updateInspectionItem(item.id, 'notes', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`${item.name} 관련 특이사항을 입력하세요.`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* 점검자 정보 및 서명 */}
          <div className="border-t pt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">점검 완료 및 서명</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 점검자 정보 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">점검자 정보</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      점검자 성명
                    </label>
                    <input
                      type="text"
                      value={inspectorName}
                      onChange={(e) => setInspectorName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={
                        room.currentResident?.name 
                          ? `기본값: ${room.currentResident.name} (입주 대상자)`
                          : inspectorName
                          ? `기본값: ${inspectorName} (추첨 당첨자)`
                          : "점검자 이름을 입력하세요"
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {room.currentResident?.name 
                        ? `입주 대상자(${room.currentResident.name})의 이름이 자동으로 입력되었습니다. 필요시 수정 가능합니다.`
                        : inspectorName
                        ? `추첨 당첨자의 이름이 자동으로 입력되었습니다. 필요시 수정 가능합니다.`
                        : "점검을 수행하는 담당자의 이름을 입력해주세요."
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* 서명 패드 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">서명</h3>
                <div 
                  className="border-2 border-gray-300 rounded-md overflow-hidden bg-white"
                  style={{ 
                    width: `${signatureSize.width}px`, 
                    height: `${signatureSize.height}px`,
                    maxWidth: '100%'
                  }}
                >
                  <SignatureCanvas
                    ref={sigCanvasRef}
                    canvasProps={{
                      width: signatureSize.width,
                      height: signatureSize.height,
                      className: 'signature-canvas',
                      style: { 
                        width: '100%', 
                        height: '100%',
                        display: 'block'
                      }
                    }}
                    backgroundColor="white"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                <button
                  onClick={clearSignature}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  서명 지우기
                </button>
                  <span className="text-xs text-gray-500 self-center">
                    서명 영역: {signatureSize.width}×{signatureSize.height}px
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 완료 조건 안내 */}
          {(!inspectionItems.every(item => item.checked) || !inspectorName.trim()) && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">완료 조건</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc pl-5 space-y-1">
                      {!inspectionItems.every(item => item.checked) && (
                        <li>모든 점검 항목에 체크해주세요 ({inspectionItems.filter(item => item.checked).length}/{inspectionItems.length})</li>
                      )}
                      {!inspectorName.trim() && (
                        <li>점검자 성명을 입력해주세요</li>
                      )}
                      <li>서명을 완료해주세요</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 완료 버튼 */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              onClick={goToRoomsList}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              disabled={saving}
            >
              취소
            </button>
            <button
              onClick={saveInspection}
              disabled={saving || !inspectionItems.every(item => item.checked) || !inspectorName.trim()}
              className={`px-6 py-2 rounded-md ${
                saving || !inspectionItems.every(item => item.checked) || !inspectorName.trim()
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saving ? '저장 중...' : '입주점검 완료'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoveInInspectionPage; 
